import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { getExistingCallClient, useCallClient } from "components/remoteAccess/callClient";
import { CallClientId, CallClientOptions } from "components/remoteAccess/helpers";
import { RemoteAccessSendEmail } from "components/remoteAccess/RemoteAccessSendEmail";
import { StyledContainer } from "components/StyledContainer";
import Transition from "components/Transition";
import { isAbortError, isEmpty } from "helpers";
import { JobStatus, TechnicianType } from "operations/schema/schema";
import Peer from "peerjs";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { getTechnicians } from "store/slices/cache.store";
import {
  acceptJob,
  addWorkTime,
  refreshJob,
  selectSelectedJob,
  selectSelectedJobVisit,
  startVisitWork,
} from "store/slices/jobs.store";
import {
  sendRemoteAccessEmail,
  setPeerId,
  setRemoteAccessOpen,
  setWaitingFor,
} from "store/slices/remoteAccess.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { RemoteAccess } from "./RemoteAccess";

export const RemoteAccessDialog: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectSelectedJob);
  const { workTimes } = useAppSelector(selectSelectedJobVisit);
  const { open, loading, peerId } = useAppSelector((s) => s.remoteAccess);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getTechnicians());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(setRemoteAccessOpen({ open: false }));
    getExistingCallClient(CallClientId)?.then?.((client) => {
      client?.endPeer?.();
    });
  }, [dispatch]);

  const onSendCustomer = useCallback(
    (email: string) => {
      if (!peerId) return;
      console.log("send email");
      dispatch(setWaitingFor({ waitingFor: email }));
      dispatch(
        sendRemoteAccessEmail({
          peerId: peerId,
          jobId: job.id,
          email,
        }),
      );
      setEmailSent(true);
    },
    [dispatch, job.id, peerId],
  );

  const onSendTechnician = useCallback(
    (technician: TechnicianType) => {
      if (!peerId) return;
      console.log("send email");
      dispatch(setWaitingFor({ waitingFor: technician.name! }));
      setEmailSent(true);
    },
    [dispatch, peerId],
  );

  const onSetPeerId = useCallback(
    (peerId: string | null) => {
      dispatch(setPeerId({ peerId }));
    },
    [dispatch],
  );
  const onPeerReady = useCallback(
    (peer: Peer) => {
      console.log("RemoteAccess: onPeerReady");
      onSetPeerId(peer.id);
    },
    [onSetPeerId],
  );

  const startWork = useCallback(async () => {
    await dispatch(addWorkTime());
    if (isEmpty(workTimes)) {
      if (job.status !== JobStatus.Accepted) {
        dispatch(
          acceptJob({
            jobId: job.id,
          }),
        )
          .then(unwrapResult)
          .then(() => {
            dispatch(refreshJob());
          });
      }
    }
    dispatch(startVisitWork({ jobId: job.id }))
      .then(unwrapResult)
      .then(({ queued }) => {
        if (queued) {
          dispatch(addSnackbarMessage({ key: "StartVisitWork-stored" }));
        } else {
          dispatch(addSnackbarMessage({ key: "StartVisitWork-success" }));
        }
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        dispatch(addSnackbarMessage({ key: "StartVisitWork-fail" }));
      });
  }, [dispatch, job.id, job.status, workTimes]);

  const onPeerDestroyed = useCallback(
    (_: Peer) => {
      console.log("RemoteAccess: onPeerDestroyed");
      onSetPeerId("");
    },
    [onSetPeerId],
  );

  useCallClient(CallClientId, {
    debug: "RemoteAccess",
    clientOptions: CallClientOptions,
    setPeerId: onSetPeerId,
    onPeerReady,
    onPeerDestroyed,
  });

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="RemoteAccessDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="RemoteAccessDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            {`${job.externalId} `} / <FormattedMessage id="remoteAccess.title" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      {!emailSent && (
        <StyledContainer style={{ height: "100%" }}>
          <RemoteAccessSendEmail
            initialEmail={job?.preferredContact?.email}
            onSendTechnician={onSendTechnician}
            onSendCustomer={onSendCustomer}
            onCancel={handleClose}
          />
        </StyledContainer>
      )}
      {emailSent && <RemoteAccess isSharing={false} onEnd={handleClose} onStartWork={startWork} />}
    </Dialog>
  );
};
